import { useEffect, useState, Fragment } from "react";
import { Plus, Trash, Scales, CurrencyDollar, Receipt, ArrowClockwise, Flag, CheckCircle, Paperclip } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, money, shortDate, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";

function Panel({ children, className = "" }) {
  return <div className={`bg-card border border-border rounded-md p-6 ${className}`}>{children}</div>;
}

/* ---------------- Determination (FA vs POS) ---------------- */
export function DeterminationTab({ c, isAdmin, reload }) {
  const d = c.determination || {};
  const [form, setForm] = useState({
    type: d.type || "",
    public_benefit: d.public_benefit ?? false,
    acquire_services: d.acquire_services ?? false,
    substantial_involvement: d.substantial_involvement ?? false,
    competitive: d.competitive ?? false,
    federal_funds: d.federal_funds ?? false,
    justification: d.justification || "",
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const suggestion = form.public_benefit && !form.acquire_services ? "FA" : (form.acquire_services ? "POS" : null);

  const save = async () => {
    if (!form.type) return toast.error("Select a determination outcome (FA or POS)");
    try {
      await api.put(`/contracts/${c.id}/determination`, form);
      toast.success("Determination saved");
      reload();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <Panel className="max-w-3xl">
      <div className="flex items-center gap-2 mb-1"><Scales size={20} className="text-primary" /><span className="font-head font-semibold">FA vs POS Determination</span></div>
      <p className="text-sm text-muted-foreground mb-5">Classify the procurement before the record can advance to review. Financial Assistance (FA) provides public benefit; Purchase of Service (POS) acquires services for the department.</p>

      <div className="space-y-4">
        {[
          ["public_benefit", "Primary purpose is to provide assistance / benefit to the public"],
          ["acquire_services", "Primary purpose is to acquire services for the department's direct benefit"],
          ["substantial_involvement", "Department expects substantial programmatic involvement"],
          ["competitive", "Awarded through a competitive process"],
          ["federal_funds", "Federal funds are involved"],
        ].map(([k, label]) => (
          <div key={k} className="flex items-center justify-between border-b border-border pb-3">
            <span className="text-sm">{label}</span>
            <Switch data-testid={`det-${k}`} checked={form[k]} onCheckedChange={(v) => set(k, v)} disabled={!isAdmin} />
          </div>
        ))}
      </div>

      {suggestion && <div className="mt-4 text-sm text-primary">Suggested classification based on answers: <b>{suggestion}</b></div>}

      <div className="mt-5 space-y-1.5">
        <Label className="overline text-muted-foreground">Determination outcome</Label>
        <RadioGroup value={form.type} onValueChange={(v) => set("type", v)} className="flex gap-6" disabled={!isAdmin}>
          <label className="flex items-center gap-2 cursor-pointer"><RadioGroupItem value="FA" data-testid="det-type-fa" /> Financial Assistance (FA)</label>
          <label className="flex items-center gap-2 cursor-pointer"><RadioGroupItem value="POS" data-testid="det-type-pos" /> Purchase of Service (POS)</label>
        </RadioGroup>
      </div>

      <div className="mt-4 space-y-1.5">
        <Label className="overline text-muted-foreground">Justification</Label>
        <Textarea data-testid="det-justification" value={form.justification} onChange={(e) => set("justification", e.target.value)} disabled={!isAdmin} placeholder="Explain the basis for this determination…" />
      </div>

      {d.determined_by && <div className="text-xs text-muted-foreground mt-3">Last determined by {d.determined_by} · {shortDate(d.determined_at)}</div>}
      {isAdmin && <div className="mt-5"><Button data-testid="det-save" onClick={save}>Save Determination</Button></div>}
    </Panel>
  );
}

/* ---------------- Structured Budget ---------------- */
const LINE_CATEGORIES = ["Personnel", "Fringe", "Travel", "Equipment", "Supplies", "Contractual", "Indirect", "Other"];

export function StructuredBudgetTab({ c, isAdmin, reload }) {
  const b = c.budget || {};
  const [items, setItems] = useState(b.line_items || []);
  const [salary, setSalary] = useState(b.salary_sheet || []);
  const [notes, setNotes] = useState(b.notes || "");

  const salCost = (r) => (Number(r.annual_salary) || 0) * ((Number(r.fte_pct) || 0) / 100) * ((Number(r.months) || 0) / 12);
  const total = items.reduce((s, i) => s + (Number(i.amount) || 0), 0) + salary.reduce((s, r) => s + salCost(r), 0);

  const addItem = () => setItems([...items, { category: "Other", description: "", amount: 0 }]);
  const addSalary = () => setSalary([...salary, { name: "", title: "", annual_salary: 0, fte_pct: 100, months: 12 }]);

  const save = async () => {
    try {
      await api.put(`/contracts/${c.id}/budget-structured`, { line_items: items, salary_sheet: salary, notes });
      toast.success("Budget saved");
      reload();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-5">
      <Panel>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2"><CurrencyDollar size={20} className="text-primary" /><span className="font-head font-semibold">Salary Sheet</span></div>
          {isAdmin && <Button size="sm" variant="outline" data-testid="add-salary" onClick={addSalary}><Plus size={14} className="mr-1" /> Add position</Button>}
        </div>
        {salary.length === 0 ? <p className="text-sm text-muted-foreground">No positions added.</p> : (
          <Table>
            <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Title</TableHead><TableHead>Annual Salary</TableHead><TableHead>FTE %</TableHead><TableHead>Months</TableHead><TableHead>Cost</TableHead><TableHead></TableHead></TableRow></TableHeader>
            <TableBody>
              {salary.map((r, i) => (
                <TableRow key={i} data-testid={`salary-row-${i}`}>
                  <TableCell><Input value={r.name} disabled={!isAdmin} onChange={(e) => { const n = [...salary]; n[i].name = e.target.value; setSalary(n); }} /></TableCell>
                  <TableCell><Input value={r.title} disabled={!isAdmin} onChange={(e) => { const n = [...salary]; n[i].title = e.target.value; setSalary(n); }} /></TableCell>
                  <TableCell><Input type="number" className="w-28" value={r.annual_salary} disabled={!isAdmin} onChange={(e) => { const n = [...salary]; n[i].annual_salary = e.target.value; setSalary(n); }} /></TableCell>
                  <TableCell><Input type="number" className="w-20" value={r.fte_pct} disabled={!isAdmin} onChange={(e) => { const n = [...salary]; n[i].fte_pct = e.target.value; setSalary(n); }} /></TableCell>
                  <TableCell><Input type="number" className="w-20" value={r.months} disabled={!isAdmin} onChange={(e) => { const n = [...salary]; n[i].months = e.target.value; setSalary(n); }} /></TableCell>
                  <TableCell className="text-sm font-medium whitespace-nowrap">{money(salCost(r))}</TableCell>
                  <TableCell>{isAdmin && <Button size="icon" variant="ghost" data-testid={`del-salary-${i}`} onClick={() => setSalary(salary.filter((_, x) => x !== i))}><Trash size={15} className="text-alert" /></Button>}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Panel>

      <Panel>
        <div className="flex items-center justify-between mb-4">
          <span className="font-head font-semibold">Budget Line Items</span>
          {isAdmin && <Button size="sm" variant="outline" data-testid="add-line" onClick={addItem}><Plus size={14} className="mr-1" /> Add line</Button>}
        </div>
        {items.length === 0 ? <p className="text-sm text-muted-foreground">No line items added.</p> : (
          <Table>
            <TableHeader><TableRow><TableHead>Category</TableHead><TableHead>Description</TableHead><TableHead>Amount</TableHead><TableHead></TableHead></TableRow></TableHeader>
            <TableBody>
              {items.map((it, i) => (
                <TableRow key={i} data-testid={`line-row-${i}`}>
                  <TableCell>
                    <Select value={it.category} onValueChange={(v) => { const n = [...items]; n[i].category = v; setItems(n); }} disabled={!isAdmin}>
                      <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
                      <SelectContent>{LINE_CATEGORIES.map((cat) => <SelectItem key={cat} value={cat}>{cat}</SelectItem>)}</SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell><Input value={it.description} disabled={!isAdmin} onChange={(e) => { const n = [...items]; n[i].description = e.target.value; setItems(n); }} /></TableCell>
                  <TableCell><Input type="number" className="w-32" value={it.amount} disabled={!isAdmin} onChange={(e) => { const n = [...items]; n[i].amount = e.target.value; setItems(n); }} /></TableCell>
                  <TableCell>{isAdmin && <Button size="icon" variant="ghost" data-testid={`del-line-${i}`} onClick={() => setItems(items.filter((_, x) => x !== i))}><Trash size={15} className="text-alert" /></Button>}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
        <div className="flex items-center justify-between mt-5 pt-4 border-t border-border">
          <span className="overline text-muted-foreground">Computed Budget Total</span>
          <span data-testid="budget-total-computed" className="font-head font-bold text-2xl text-primary">{money(total)}</span>
        </div>
      </Panel>

      <Panel>
        <Label className="overline text-muted-foreground">Notes / instructions</Label>
        <Textarea data-testid="budget-notes" className="mt-1.5" value={notes} onChange={(e) => setNotes(e.target.value)} disabled={!isAdmin} />
        {isAdmin && <div className="mt-4"><Button data-testid="budget-save" onClick={save}>Save Budget</Button></div>}
      </Panel>
    </div>
  );
}

/* ---------------- Reimbursements / FSR ---------------- */
const REIMB_STATUS = {
  submitted: "bg-sky-50 text-sky-800 border-sky-300",
  under_review: "bg-amber-50 text-amber-800 border-amber-300",
  approved: "bg-violet-50 text-violet-800 border-violet-300",
  paid: "bg-emerald-50 text-emerald-700 border-emerald-300",
  rejected: "bg-alert/10 text-alert border-alert/40",
};

export function ReimbursementsTab({ c, user, reload }) {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ period_label: "", amount_requested: "", date_submitted: "", notes: "" });
  const canReview = ["grant_manager", "admin", "contract_administrator"].includes(user.role);
  const canSubmit = ["contract_administrator", "admin"].includes(user.role);

  const load = async () => setRows((await api.get(`/contracts/${c.id}/reimbursements`)).data);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [c.id]);

  const submit = async () => {
    if (!form.period_label) return toast.error("Period label required");
    await api.post(`/contracts/${c.id}/reimbursements`, { ...form, amount_requested: parseFloat(form.amount_requested) || 0 });
    toast.success("Reimbursement submitted");
    setForm({ period_label: "", amount_requested: "", date_submitted: "", notes: "" });
    load();
  };
  const setStatus = async (rid, status) => { await api.put(`/reimbursements/${rid}`, { status }); load(); };
  const markRead = async (r) => { if (!r.unread_count) return; try { await api.post(`/reimbursements/${r.id}/mark-read`); load(); } catch {} };
  const [replies, setReplies] = useState({});
  const sendReply = async (rid) => {
    const msg = (replies[rid] || "").trim();
    if (!msg) return;
    await api.post(`/reimbursements/${rid}/reply`, { message: msg });
    setReplies({ ...replies, [rid]: "" });
    toast.success("Reply sent to grantee");
    load();
  };
  const remove = async (rid) => { await api.delete(`/reimbursements/${rid}`); load(); };
  const reviewDoc = async (docId, status) => {
    try { await api.put(`/documents/${docId}/review`, { status }); toast.success(`Document ${status}`); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const DOC_PILL = { approved: "bg-emerald-50 text-emerald-700 border-emerald-300", rejected: "bg-alert/10 text-alert border-alert/40", pending: "bg-amber-50 text-amber-700 border-amber-300" };
  const BE = process.env.REACT_APP_BACKEND_URL;

  return (
    <div className="space-y-5">
      {canSubmit && (
        <Panel>
          <div className="flex items-center gap-2 mb-4"><Receipt size={20} className="text-primary" /><span className="font-head font-semibold">Log Reimbursement / FSR</span></div>
          <div className="grid sm:grid-cols-4 gap-3">
            <div className="space-y-1.5"><Label className="overline text-muted-foreground">Period</Label><Input data-testid="rmb-period" value={form.period_label} onChange={(e) => setForm({ ...form, period_label: e.target.value })} placeholder="Q1 FY26" /></div>
            <div className="space-y-1.5"><Label className="overline text-muted-foreground">Amount ($)</Label><Input data-testid="rmb-amount" type="number" value={form.amount_requested} onChange={(e) => setForm({ ...form, amount_requested: e.target.value })} /></div>
            <div className="space-y-1.5"><Label className="overline text-muted-foreground">Date submitted</Label><Input data-testid="rmb-date" type="date" value={form.date_submitted} onChange={(e) => setForm({ ...form, date_submitted: e.target.value })} /></div>
            <div className="flex items-end"><Button data-testid="rmb-submit" className="w-full" onClick={submit}><Plus size={15} className="mr-1" /> Submit</Button></div>
          </div>
        </Panel>
      )}

      {rows.length === 0 ? (
        <div className="border border-dashed border-border rounded-md p-8 text-center text-sm text-muted-foreground">No reimbursements logged.</div>
      ) : (
        <div className="bg-card border border-border rounded-md overflow-hidden">
          <Table>
            <TableHeader><TableRow><TableHead>Period</TableHead><TableHead>Amount</TableHead><TableHead>Submitted</TableHead><TableHead>Status</TableHead><TableHead></TableHead></TableRow></TableHeader>
            <TableBody>
              {rows.map((r) => (
                <Fragment key={r.id}>
                <TableRow data-testid={`rmb-row-${r.id}`}>
                  <TableCell className="text-sm font-medium">
                    <div className="flex items-center gap-2">
                      {r.period_label}
                      {r.unread_count > 0 && (
                        <Badge data-testid={`rmb-unread-${r.id}`} className="bg-alert text-white hover:bg-alert h-5 px-1.5 text-[10px]">{r.unread_count} new</Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-sm">{money(r.amount_requested)}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{shortDate(r.date_submitted)}</TableCell>
                  <TableCell>
                    {canReview ? (
                      <Select value={r.status} onValueChange={(v) => setStatus(r.id, v)}>
                        <SelectTrigger data-testid={`rmb-status-${r.id}`} className="w-40 h-8"><SelectValue /></SelectTrigger>
                        <SelectContent>{Object.keys(REIMB_STATUS).map((s) => <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
                      </Select>
                    ) : (
                      <span className={`inline-flex px-2.5 py-0.5 rounded-sm border text-xs font-medium ${REIMB_STATUS[r.status]}`}>{r.status.replace(/_/g, " ")}</span>
                    )}
                  </TableCell>
                  <TableCell>{canSubmit && <Button size="icon" variant="ghost" data-testid={`rmb-del-${r.id}`} onClick={() => remove(r.id)}><Trash size={15} className="text-alert" /></Button>}</TableCell>
                </TableRow>
                {(r.adjustment || "").trim() && (
                  <TableRow className="hover:bg-transparent">
                    <TableCell colSpan={5} className="pt-0 pb-2">
                      <div className="text-xs bg-gold/10 border-l-2 border-gold rounded-sm px-3 py-2" data-testid={`rmb-adjustment-${r.id}`}>
                        <span className="font-medium text-foreground">Adjustments this period: </span>
                        <span className="text-muted-foreground">{r.adjustment}</span>
                      </div>
                    </TableCell>
                  </TableRow>
                )}
                {((r.documents || []).length > 0 || (r.thread || []).length > 0) && (
                  <TableRow key={`${r.id}-docs`} data-testid={`rmb-docs-${r.id}`} className="bg-secondary/40 hover:bg-secondary/40">
                    <TableCell colSpan={5} className="py-2">
                      {(r.documents || []).length > 0 && <div className="overline text-muted-foreground mb-1.5">Grantee documents</div>}
                      <div className="space-y-1.5">
                        {(r.documents || []).map((d) => {
                          const st = d.review_status || "pending";
                          return (
                            <div key={d.id} className="flex flex-wrap items-center gap-2.5 text-sm">
                              <a href={`${BE}/api/documents/${d.id}/download`} target="_blank" rel="noreferrer" data-testid={`rmb-doc-view-${d.id}`} className="text-primary hover:underline flex items-center gap-1">
                                <Paperclip size={14} /> {d.file_name}
                              </a>
                              <span className={`inline-flex px-2 py-0.5 rounded-sm border text-[11px] font-medium capitalize ${DOC_PILL[st]}`} data-testid={`rmb-doc-status-${d.id}`}>{st}</span>
                              {canReview && st === "pending" && (
                                <>
                                  <Button size="sm" variant="outline" className="h-7" data-testid={`rmb-doc-approve-${d.id}`} onClick={() => reviewDoc(d.id, "approved")}>Approve</Button>
                                  <Button size="sm" variant="ghost" className="h-7 text-alert" data-testid={`rmb-doc-reject-${d.id}`} onClick={() => reviewDoc(d.id, "rejected")}>Reject</Button>
                                </>
                              )}
                              {d.reviewed_by && st !== "pending" && <span className="text-[11px] text-muted-foreground">by {d.reviewed_by}</span>}
                            </div>
                          );
                        })}
                      </div>
                      <div className="mt-3" data-testid={`rmb-thread-${r.id}`}>
                        <div className="overline text-muted-foreground mb-1.5">Conversation</div>
                        <div className="space-y-1.5 mb-2">
                          {(r.thread || []).length === 0 && <div className="text-xs text-muted-foreground">No messages yet.</div>}
                          {(r.thread || []).map((m, i) => (
                            <div key={i} className={`text-sm flex gap-2 ${m.by_role === "grantee" ? "" : "text-primary"}`}>
                              <span className="font-medium whitespace-nowrap">{m.by_name} ({m.by_role === "grantee" ? "Grantee" : "Reviewer"}):</span>
                              <span className="text-foreground">{m.message}</span>
                            </div>
                          ))}
                        </div>
                        <div className="flex gap-2">
                          <Input data-testid={`rmb-reply-input-${r.id}`} value={replies[r.id] || ""} onFocus={() => markRead(r)} onChange={(e) => setReplies({ ...replies, [r.id]: e.target.value })} placeholder="Reply to the grantee…" className="h-8 bg-card" />
                          <Button size="sm" className="h-8" data-testid={`rmb-reply-send-${r.id}`} onClick={() => sendReply(r.id)}>Send</Button>
                        </div>
                      </div>
                    </TableCell>
                  </TableRow>
                )}
              </Fragment>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}

/* ---------------- Renewal ---------------- */
export function RenewalTab({ c, isAdmin, reload }) {
  const r = c.renewal || {};
  const [form, setForm] = useState({ eligible: r.eligible ?? false, decision: r.decision || "pending", new_end_date: r.new_end_date || "", notes: r.notes || "" });
  useEffect(() => {
    const x = c.renewal || {};
    setForm({ eligible: x.eligible ?? false, decision: x.decision || "pending", new_end_date: x.new_end_date || "", notes: x.notes || "" });
  }, [c.renewal]);
  const daysLeft = c.end_date ? Math.round((new Date(c.end_date) - new Date()) / 86400000) : null;

  const save = async () => {
    try { await api.put(`/contracts/${c.id}/renewal`, form); toast.success("Renewal saved"); reload(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <Panel className="max-w-2xl">
      <div className="flex items-center gap-2 mb-1"><ArrowClockwise size={20} className="text-primary" /><span className="font-head font-semibold">Renewal</span></div>
      <div className={`text-sm mb-5 ${daysLeft !== null && daysLeft <= 30 ? "text-alert" : "text-muted-foreground"}`}>
        Current end date: <b>{shortDate(c.end_date)}</b>{daysLeft !== null && ` · ${daysLeft} day(s) remaining`}
      </div>
      <div className="space-y-4">
        <div className="flex items-center justify-between border-b border-border pb-3">
          <span className="text-sm">Eligible for renewal</span>
          <Switch data-testid="renew-eligible" checked={form.eligible} onCheckedChange={(v) => setForm({ ...form, eligible: v })} disabled={!isAdmin} />
        </div>
        <div className="space-y-1.5">
          <Label className="overline text-muted-foreground">Renewal decision</Label>
          <Select value={form.decision} onValueChange={(v) => setForm({ ...form, decision: v })} disabled={!isAdmin}>
            <SelectTrigger data-testid="renew-decision"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="renew">Renew</SelectItem>
              <SelectItem value="not_renew">Do not renew</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {form.decision === "renew" && (
          <div className="space-y-1.5">
            <Label className="overline text-muted-foreground">New end date</Label>
            <Input data-testid="renew-new-end" type="date" value={form.new_end_date} onChange={(e) => setForm({ ...form, new_end_date: e.target.value })} disabled={!isAdmin} />
          </div>
        )}
        <div className="space-y-1.5">
          <Label className="overline text-muted-foreground">Notes</Label>
          <Textarea data-testid="renew-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} disabled={!isAdmin} />
        </div>
        {isAdmin && <Button data-testid="renew-save" onClick={save}>Save Renewal</Button>}
      </div>
    </Panel>
  );
}

/* ---------------- Closeout ---------------- */
const CLOSEOUT_STEPS = [
  ["final_report", "Final progress report received"],
  ["final_reimbursement", "Final reimbursement processed"],
  ["deliverables_accepted", "All deliverables accepted"],
  ["documents_archived", "Documents archived"],
];

export function CloseoutTab({ c, isAdmin, reload }) {
  const co = c.closeout || {};
  const [status, setStatus] = useState(co.status || "not_started");
  const [checklist, setChecklist] = useState(co.checklist || {});
  const [notes, setNotes] = useState(co.notes || "");
  useEffect(() => {
    const x = c.closeout || {};
    setStatus(x.status || "not_started");
    setChecklist(x.checklist || {});
    setNotes(x.notes || "");
  }, [c.closeout]);
  const done = CLOSEOUT_STEPS.filter(([k]) => checklist[k]).length;

  const save = async (newStatus) => {
    const s = newStatus || status;
    if (s === "complete" && done < CLOSEOUT_STEPS.length) return toast.error("Complete all closeout steps before finishing");
    try { await api.put(`/contracts/${c.id}/closeout`, { status: s, checklist, notes }); toast.success("Closeout saved"); reload(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <Panel className="max-w-2xl">
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2"><Flag size={20} className="text-primary" /><span className="font-head font-semibold">Closeout</span></div>
        <Badge variant="secondary">{status.replace(/_/g, " ")}</Badge>
      </div>
      <p className="text-sm text-muted-foreground mb-5">{done}/{CLOSEOUT_STEPS.length} steps complete. Marking complete sets the contract to closed.</p>
      <div className="space-y-3">
        {CLOSEOUT_STEPS.map(([k, label]) => (
          <label key={k} className="flex items-center gap-3 border-b border-border pb-3 cursor-pointer">
            <Checkbox data-testid={`closeout-${k}`} checked={!!checklist[k]} onCheckedChange={(v) => setChecklist({ ...checklist, [k]: !!v })} disabled={!isAdmin} />
            <span className="text-sm">{label}</span>
            {checklist[k] && <CheckCircle size={16} weight="fill" className="text-emerald-600 ml-auto" />}
          </label>
        ))}
      </div>
      <div className="space-y-1.5 mt-4">
        <Label className="overline text-muted-foreground">Notes</Label>
        <Textarea data-testid="closeout-notes" value={notes} onChange={(e) => setNotes(e.target.value)} disabled={!isAdmin} />
      </div>
      {isAdmin && (
        <div className="flex gap-2 mt-5">
          <Button variant="outline" data-testid="closeout-save" onClick={() => save("in_progress")}>Save Progress</Button>
          <Button data-testid="closeout-complete" onClick={() => save("complete")}><CheckCircle size={16} className="mr-1.5" /> Mark Closed</Button>
        </div>
      )}
    </Panel>
  );
}
